import React, { useEffect, useRef, useState } from 'react';
import { Inspiration, Contest, User } from '../../global';
import { useAuth } from '../../context/AuthContext';
import { useBanner } from '../../context/BannerContext';
import Dropzone from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleQuestion, faCircleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './InspirationEditor.module.scss';
import axiosHelper from '../../helpers/axiosHelper';
import axios from 'axios';
import ContestSelect from '../Forms/DropDown/ContestSelect';

interface InspirationEditorProps {
  inspiration: Inspiration | null;
  contest?: Contest;
  user?: User;
  closeCallback?: () => void;
  saveCallback?: (inspiration: Inspiration) => void;
}

const InspirationEditor: React.FC<InspirationEditorProps> = ({ inspiration, contest, closeCallback, saveCallback }) => {
  const imageMimesToAccept = { 'image/png': ['.png'], 'image/jpg': ['.jpg'], 'image/jpeg': ['.jpeg'] };
  const { createAndAddBannerMessage } = useBanner();
  const isEditing = Boolean(inspiration?.id);
  const [deletedImage, setDeletedImage] = useState(false);
  // Convert boolean isPrivate to string for radio buttons, default to 'false' (public)
  const defaultFormValues: any = inspiration 
    ? { ...inspiration, isPrivate: inspiration.isPrivate ? 'true' : 'false' } 
    : { isPrivate: 'false' };

  const [popupURL, setPopupURL] = useState(false);
  const [popupDescription, setPopupDescription] = useState(false);
  const [popupPrivacy, setPopupPrivacy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<Partial<Inspiration>>({
    defaultValues: defaultFormValues,
  });
  const [urlError, setUrlError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File>();
  const [openContests, setOpenContests] = useState<Contest[]>([]);

  useEffect(() => {
    if (contest) {
      setOpenContests([contest]);
    } else {
    }
  }, [contest]);

  const presignAndUpload = async (file: File) => {
    const response = await axiosHelper.getPresignedUrl(0, file.name, file.size, file.type);
    if (response) {
      const uploadResponse = await axios.put(response.data.payload, file);
      if (uploadResponse) {
        return response.data.id;
      }
    }
    return null;
  };

  const onImageDrop = (e: any) => {
    // console.log(e?.[0]);
    setUploadedImage(e?.[0]);
    if (e?.[0]) {
      setUrlError(false);
      setImageError(false);
    }
    // imagePresignedUrl(e?.[0]);
  };

  const onSubmit: SubmitHandler<Partial<Inspiration>> = async (data) => {
    console.log('Inspiration data to submit:', data);

    // Validate that either URL or image is provided
    if (!data.websiteURL && !uploadedImage && !inspiration?.imageAttachment) {
      setUrlError(true);
      setImageError(true);
      return;
    }

    // Reset error states
    setUrlError(false);
    setImageError(false);

    // Convert isPrivate from string to boolean
    if (typeof data.isPrivate === 'string') {
      data.isPrivate = data.isPrivate === 'true';
    }

    if (uploadedImage) {
      const presignedId = await presignAndUpload(uploadedImage);
      if (presignedId) {
        data.tempUploads = presignedId;
      }
    }
    if (contest && !data.contest) {
      data.contest = contest;
    }
    if (deletedImage) {
      data.deleteImage = 'true';
    }
    const newInspiration = await axiosHelper.createInspiration(data as Inspiration);
    if (newInspiration) {
      createAndAddBannerMessage('Inspiration saved successfully', 'success', 'inspiration-saved');
      if (saveCallback) {
        saveCallback(newInspiration);
      }
    }
  };
  return (
    // <div className="col-12 col-md-9 pt-5 px-5 px-sm-4">
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row">
        <div className="col-12 col-md-3 px-4 position-relative">&nbsp;</div>

        <div className="col-12 col-md-9 px-4">
          {contest ? null : (
            <div className="row mb-5">
              <div className="col-12 col-md-2 text fw-bold mb-3 ">
                <div className=" d-flex justify-content-between" style={{ position: 'relative', zIndex: 10 }}>
                  Inspiration for
                </div>
              </div>
              <div className="col-12 col-md-10">
                <Controller
                  name="contest"
                  control={control}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <>
                      <ContestSelect value={value} onChange={onChange} />
                      {error && <span className="color-red">{error.message}</span>} {/* Display validation error */}
                    </>
                  )}
                />
              </div>
            </div>
          )}

          <div className="row mb-5">
            <div className="col-12 col-md-2 text fw-bold mb-3 ">
              <div className=" d-flex justify-content-between" style={{ position: 'relative', zIndex: 10 }}>
                URL
                <div className="ml-auto">
                  <FontAwesomeIcon
                    icon={faCircleQuestion as IconProp}
                    className=" text color-blue me-0"
                    onMouseEnter={() => setPopupURL(true)}
                    onMouseLeave={() => setPopupURL(false)}
                  />
                  <div className={`${popupURL ? styles.message : styles.noMessage}`}>
                    <div className={`  ${styles.freccetta} `}></div>
                    <div className={`${styles.infoActions} text p-4`}>
                      <i>Please insert a complete URL here or an image below</i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-10">
              <input 
                type="text" 
                {...register('websiteURL', {
                  validate: (value) => {
                    if (!value) return true; // Allow empty since it's optional
                    return value.startsWith('http://') || value.startsWith('https://') || 'URL must start with http:// or https://';
                  }
                })} 
                placeholder="Please use the form https:// " 
                className="text-start" 
              />
              {errors.websiteURL && <span className="color-dark_red">{errors.websiteURL.message}</span>}
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-12 col-md-2 text fw-bold mb-3">
              <div className=" d-flex justify-content-between ">
                <div className="mt-3">Image file</div>
              </div>
            </div>
            <div className={`col-12  col-md-10 ${uploadedImage ? '' : 'mb-5'} d-flex flex-column align-items-center justify-content-center  ${styles.uploadCoverContainer}`}>
              {/* <label
                              htmlFor="images"
                              className={`${styles.dropcontainercoverimages} d-flex position-relative flex-column justify-content-center align-items-center text-center px-5`}
                            > */}
              <Dropzone onDrop={onImageDrop} accept={imageMimesToAccept} maxSize={31457280}>
                {({ getRootProps, getInputProps }) => (
                  <section className={`${styles.dropzone}  bg-light d-flex align-items-center justify-content-center`} {...getRootProps()}>
                    <div className="col-12 d-flex  flex-column align-items-center justify-content-center text-center">
                      <input {...getInputProps()} />
                      <span>
                        Drag and drop an image or <span className="color-blue">choose a file</span> from your computer <br />
                        (allowed formats: .jpg, .png)
                      </span>
                    </div>
                  </section>
                )}
              </Dropzone>
              {imageError && (
                <span className="color-dark_red d-flex flex-row align-items-center mt-2 justify-content-center">
                  <FontAwesomeIcon icon={faCircleExclamation} className="color-dark_red me-2" /> please provide an URL or an image
                </span>
              )}
            </div>
          </div>
          <div className={`row mb-5 ${uploadedImage ? '' : 'd-none'} `}>
            <div className="col-12 col-md-2 text fw-bold mb-3">
              <div className=" d-flex justify-content-between "></div>
            </div>
            <div className={`col-12 col-md-10 mb-5 d-flex flex-column align-items-center justify-content-center  `}>
              {uploadedImage && (
                <div className=" d-flex" style={{ width: '100%' }}>
                  <div className={`${styles.previewImg}`}>
                    <img src={URL.createObjectURL(uploadedImage)} className={`${styles.previewImg}`} />
                  </div>
                  <div className="d-flex flex-row w-100 align-items-center justify-content-center ">
                    <span className="text-xl">{uploadedImage.name}</span>
                  </div>
                  <div className=" d-flex align-items-center justify-content-end ">
                    <div
                      // className="btn-danger"
                      className={`${styles.removeButton}`}
                      style={{ borderRadius: '8px' }}
                      onClick={() => setUploadedImage(undefined)}
                    >
                      <FontAwesomeIcon icon={faXmark as IconProp} className=" text-16 me-0" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {inspiration?.imageAttachment && !deletedImage && (
            <div className={`row mb-5`}>
              <div className="col-12 col-md-2 text fw-bold mb-3">
                <div className=" d-flex justify-content-between "></div>
              </div>
              <div className={`col-12 col-md-10 mb-5 d-flex flex-column align-items-center justify-content-center  `}>
                <div className=" d-flex" style={{ width: '100%' }}>
                  <div className={`${styles.previewImg}`}>
                    <img src={inspiration.imageAttachment.s3Path} className={`${styles.previewImg}`} />
                  </div>
                  <div className="d-flex flex-row w-100 align-items-center justify-content-center ">
                    <span className="text-xl">{inspiration.imageAttachment.originalFileName}</span>
                  </div>
                  <div className=" d-flex align-items-center justify-content-end ">
                    <div
                      // className="btn-danger"
                      className={`${styles.removeButton}`}
                      style={{ borderRadius: '8px' }}
                      onClick={() => setDeletedImage(true)}
                    >
                      <FontAwesomeIcon icon={faXmark as IconProp} className=" text-16 me-0" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="row mb-5">
            <div className="col-12 col-md-2 text fw-bold mb-3 ">
              <div className=" d-flex justify-content-between" style={{ position: 'relative', zIndex: 10 }}>
                Description
                <div className=" ml-auto">
                  <FontAwesomeIcon
                    icon={faCircleQuestion as IconProp}
                    className=" text color-blue me-0 "
                    onMouseEnter={() => setPopupDescription(true)}
                    onMouseLeave={() => setPopupDescription(false)}
                  />
                  <div className={`${popupDescription ? styles.message : styles.noMessage}`}>
                    <div className={`${styles.freccetta} `}></div>
                    <div className={`${styles.infoActions} text p-4`}>
                      <i>Some explanatory text, limit 140 characters.</i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-10  ">
              <textarea placeholder="maximum 140 characters" required={false} {...register('textDescription', { maxLength: 140 })} />
            </div>
          </div>

          <div className="row mb-5">
            {/* <div className="col-12  justify-content-center col-md-2 text align-middle fw-bold mb-3"> */}
            <div className="col-12 col-md-2 text fw-bold mb-3 ">
              <div className=" d-flex justify-content-between" style={{ position: 'relative', zIndex: 10 }}>
                Content privacy
                <div className="ml-auto">
                  <FontAwesomeIcon
                    icon={faCircleQuestion as IconProp}
                    className=" text color-blue me-0"
                    onMouseEnter={() => setPopupPrivacy(true)}
                    onMouseLeave={() => setPopupPrivacy(false)}
                  />
                  <div className={`${popupPrivacy ? styles.message : styles.noMessage}`}>
                    <div className={`  ${styles.freccetta} `}></div>
                    <div className={`${styles.infoActions} text p-4`}>
                      <i>
                        This option allows you to decide if your content will be private or available also for other users. If you want to boost your reputation, choose the public
                        option.
                      </i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-10">
              <input type="radio" className="privacy align-middle me-4" value="false" {...register('isPrivate', { required: 'Please select a privacy option' })} />
              <span className=" me-5"> Public </span>
              &nbsp;
              <input type="radio" className="privacy align-middle me-4" value="true" {...register('isPrivate', { required: 'Please select a privacy option' })} />
              <span className="me-5"> Private </span>
              {errors.isPrivate && (
                <div className="color-dark_red mt-2">
                  <FontAwesomeIcon icon={faCircleExclamation} className="color-dark_red me-2" />
                  {errors.isPrivate.message}
                </div>
              )}
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-12  col-md-2 mb-3"></div>
            <div className="col-12 col-md-10">
              <div className=" d-flex align-items-center justify-content-center">
                <button type="submit" className="btn-blue rounded col-md-3 ">{isEditing ? 'Update' : 'Publish'} </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default InspirationEditor;
